/**
 * Ein Abschnitt der Anlagen-Auswahl — „Feuer, Brand" bzw. „Elektrische Anlagen
 * und Betriebsmittel allgemein" — als Bildkachel-Raster.
 *
 * Die beiden Abschnitte sind ABSCHNITTE innerhalb der einen Frage „Welche der
 * folgenden Anlagen sind vorhanden (Mehrfachauswahl möglich)?" und ausdrücklich
 * keine eigenen Fragen — der Titel ist deshalb eine Zwischenüberschrift (h4)
 * unter der Frage (h3) und keine zweite Fragestellung.
 *
 * Die Komponente kennt keine einzige Anlage beim Namen: sie liest den Katalog
 * und rendert je Eintrag genau EINEN `AnlagenBlock`. Der Marker („**" ohne
 * Errichter) und die Zusatzfelder kommen aus den Daten.
 *
 * **23 Kacheln sind viel — deshalb drei Vorkehrungen:**
 *  1. Das Raster misst die **Container**-Breite (`auto-fit` + `minmax(min(…,
 *     100%), 1fr))`), nicht den Viewport: das Formular läuft auch im
 *     schwebenden Fenster, das sich bis 420 px herunterziehen lässt, während
 *     der Viewport breit bleibt. Eine Viewport-Regel lieferte dort drei
 *     Kacheln in einem ~350 px schmalen Bereich.
 *  2. Die beiden Abschnitte sind sichtbar getrennt — Haarlinie darüber,
 *     Goldmarke und Zähler in der Kopfzeile. Ohne das liefen 23 gleich
 *     aussehende Kacheln zu einer einzigen Fläche zusammen, in der die
 *     Zwischenüberschrift untergeht.
 *  3. Der Zähler („3 von 16 gewählt") steht neben der Überschrift, weil die
 *     Auswahl bei aufgeklappten Kacheln über mehrere Bildschirmhöhen reicht:
 *     was gewählt ist, wäre sonst nur durch Scrollen zu ermitteln. Er steht
 *     NEBEN der Überschrift und nicht in ihr — der Abschnitt trägt seinen
 *     Namen über `aria-labelledby`, und eine mitwandernde Zahl im Namen
 *     ließe den Vorlesenamen bei jedem Klick springen.
 */
import { useId } from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import AnlagenBlock, { type AnlagenEntry } from "./AnlagenBlock";
import {
  anlagenAnswerKey,
  anlagenDerGruppe,
  anlagenSlotKey,
  zeigtErrichter,
  type AnlagenGruppeKey,
} from "./anlagenKatalog";
import type { Ansprache } from "./texte";
import type { UploadScope } from "./uploadBuffer";
import { anlageSichtbar } from "./nutzungsWeiche";

/**
 * Untergrenze einer Kachel.
 *
 * Gerechnet auf die auf 920 px gedeckelte Lesespalte
 * (`pages/RisikoImmobilieStepForm.tsx`, Stil `inner`) bei 12 px Abstand:
 *   4 Spalten → 4×260 + 3×12 = 1076 px — passt nicht,
 *   3 Spalten → 3×260 + 2×12 =  804 px — passt, je Kachel ~298 px.
 * Drei Spalten sind für 16 bzw. 7 Einträge die Obergrenze, bei der die
 * Beschriftung („Notstromversorgung Netzersatzanlage (Diesel)") noch in zwei
 * bis drei Zeilen steht und die aufgeklappte Kachel ihre Felder einspaltig
 * unterbringt. Vier Spalten (~215 px) hätten die Zeichnung auf Symbolgröße
 * gedrückt und aus den Kacheln wieder eine Liste gemacht.
 *
 * Unter 640 px Containerbreite ergeben sich zwei Spalten (2×260 + 12 = 532),
 * unter 532 px eine — und `min(…, 100%)` hält das Raster auch dann im
 * Container, wenn er die 260 px selbst unterschreitet.
 */
const KACHEL_MIN_PX = 260;

const useStyles = makeStyles({
  abschnitt: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    paddingTop: "16px",
    ...shorthands.borderTop("1px", "solid", BRAND.border),
  },
  kopfzeile: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    rowGap: "4px",
    flexWrap: "wrap",
  },
  /** Goldmarke: dieselbe Hinweisfarbe, die im Formular die Detailblöcke säumt. */
  marke: {
    width: "4px",
    height: "16px",
    flexShrink: 0,
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("2px"),
  },
  titel: {
    marginTop: 0,
    marginBottom: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: BRAND.ink,
  },
  zaehler: {
    marginLeft: "auto",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  /**
   * `alignItems: start`, damit eine aufgeklappte Kachel ihre Nachbarinnen in
   * derselben Reihe nicht mitwachsen lässt — sonst zöge ein geöffneter Block
   * zwei leere Kacheln auf seine Höhe.
   */
  raster: {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fit, minmax(min(${KACHEL_MIN_PX}px, 100%), 1fr))`,
    gap: "12px",
    alignItems: "start",
  },
});

type Props = {
  gruppe: AnlagenGruppeKey;
  titel: string;
  /** Der komplette Antwort-Bag des Gebäudes. */
  answers: Record<string, unknown>;
  /** Schreibt EIN Anlagen-Merkmal; `undefined` löscht es. */
  onChange: (answerKey: string, next: AnlagenEntry | undefined) => void;
  scope: UploadScope;
  ansprache?: Ansprache;
};

const AnlagenChecklist = ({
  gruppe,
  titel,
  answers,
  onChange,
  scope,
  ansprache,
}: Props) => {
  const styles = useStyles();
  const titelId = useId();
  // Weiche Wohnen ↔ Gewerbe: beim reinen Wohnhaus nur, was dort vorkommt
  // (oder schon beantwortet ist) — siehe `nutzungsWeiche.ts`.
  const eintraege = anlagenDerGruppe(gruppe).filter((e) => anlageSichtbar(e.key, answers));
  const gewaehlt = eintraege.filter(
    (eintrag) =>
      (answers[anlagenAnswerKey(eintrag.key)] as AnlagenEntry | undefined)?.vorhanden === "ja",
  ).length;

  return (
    <section className={styles.abschnitt} aria-labelledby={titelId}>
      <div className={styles.kopfzeile}>
        <span className={styles.marke} aria-hidden="true" />
        <h4 id={titelId} className={styles.titel}>
          {titel}
        </h4>
        <span className={styles.zaehler}>
          {gewaehlt} von {eintraege.length} gewählt
        </span>
      </div>
      <div className={styles.raster}>
        {eintraege.map((eintrag) => {
          const answerKey = anlagenAnswerKey(eintrag.key);
          const wert = (answers[answerKey] as AnlagenEntry | undefined) ?? {};
          return (
            <AnlagenBlock
              key={eintrag.key}
              anlage={eintrag.key}
              title={eintrag.label}
              value={wert}
              onChange={(next) => onChange(answerKey, next)}
              scope={scope}
              slotKey={anlagenSlotKey(eintrag.key)}
              ansprache={ansprache}
              variant="checkbox"
              showErrichter={zeigtErrichter(eintrag)}
              extraFields={eintrag.extraFields}
            />
          );
        })}
      </div>
    </section>
  );
};

export default AnlagenChecklist;
